import {GridCol, GridColDef, ICellBuilder, IGrid, IGridDataHolder, IGridDataRow, IGridRowDropMgr, IRowSortFn} from "/@back@/commons/widgets/grid-core.js";
import {GridDataHolderBase, GridDataHolderJsonArray, GridDataRowJson, IGridRowInternalProps, MxGridUntouchedDatas} from "/@back@/commons/widgets/grid-libs.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {GridSmall} from "/@back@/commons/widgets/grid-tags.js";
import {EventMgr} from "/@lib@/commons/events.js";

/** Extension de IGrid pour accéder à la colonne principale gérant l'arbre. */
export interface IGridTree extends IGrid {

	dataHolder: IGridDataHolderTree;

	treeCol: IGridColTree;

	/** internal. */
	dragOverTimer?: number | any /* bug typage TS2.9 incompréhensible... */
}

export class GridColTreeDef extends GridColDef {

	protected _childrenIndent: number;

	protected _onDblclick: 'toggle' /*default*/ | 'open' | 'none'

	protected _treeSkin: string | ((col: IGridColTree) => string);

	protected _rowDropMgr: IGridRowDropMgr;

	getCellBuilder(col: GridCol): ICellBuilder {
		return new CellBuilderTreeRow(col, super.getCellBuilder(col), this._childrenIndent, this._onDblclick);
	}


	/** Spécifie l'indentation en 'em' de l'arbre lorsque les lignes ne sont pas dessinées. */
	setChildrenIndent(width: number): this {
		this._childrenIndent = width;
		return this;
	}

	setOnDblClick(onDblclick: 'toggle' /*default*/ | 'open' | 'none'): this {
		this._onDblclick = onDblclick;
		return this;
	}


	getTreeSkin(col: IGridColTree): string {return (typeof this._treeSkin === 'function') ? this._treeSkin(col) : this._treeSkin}

	/** Remplace le skin par défaut 'c-grid/tree'. */
	setTreeSkin(val: string | ((col: IGridColTree) => string)): this {
		this._treeSkin = val;
		return this;
	}

	getRowDropMgr(): IGridRowDropMgr {return this._rowDropMgr}

	setRowDropMgr(mgr: IGridRowDropMgr): this {
		this._rowDropMgr = mgr;
		return this;
	}

	initCol(col: IGridColTree) {
		super.initCol(col);
		if (col.grid.treeCol) throw Error("Only ONE treeCol can be defined in a tree: '" + col.grid.treeCol + "' and '" + col + "' declared");
		col.grid.treeCol = col;
		col.treeSkin = REG.findReg(col.grid).installSkin(this.getTreeSkin(col) || 'c-grid/tree', col.grid.shadowRoot);
		col.grid.addEventListener('keydown', onKeydown);
		if (this._rowDropMgr) col.grid.setRowDropMgr(this._rowDropMgr, onDragOverInScrollArea, onDropMarkChange);
	}

	onRemovedCol(col: IGridColTree) {
		super.onRemovedCol(col);
		col.grid.treeCol = null;
		col.treeSkin.remove();
		col.grid.removeEventListener('keydown', onKeydown);
		if (this._rowDropMgr) col.grid.setRowDropMgr(null); //reset des handlers du drop dédiés au tree.
	}

}

/** IGridDataHolder gérant une arboresence. */
export interface IGridDataHolderTree extends IGridDataHolder {

	/**
	 * Retourne l'état du dossier d'une ligne.
	 * Retourne EFolderStatus.none si ce n'est pas un dossier.
	 */
	getRowKeyFolderState(rowKey: any): EFolderState

	/** Retourne false si l'état courant du dossier ne peut être modifié. */
	isRowKeyFolderStateSwitchable(rowKey: any): boolean;

	/** Ouvre un dossier. Retourne false si déjà ouvert, n'est pas un dossier ou ne peut être ouvert. */
	openFolder(rowKey: any): boolean;

	/** Ferme un dossier. Retourne false si déjà clos, n'est pas un dossier ou ne peut être fermé. */
	closeFolder(rowKey: any): boolean;

	/**
	 * Ouvre un dossier avec retour en Promise pour les implémentations async.
	 * Retourne false si déjà ouvert, n'est pas un dossier ou ne peut être ouvert.
	 */
	openFolderAsync(rowKey: any): Promise<boolean>

	/**
	 * Inverse l'état du dossier.
	 * Retourne true si une action a bien eu lieu, false sinon (pas un dossier ou dossier pas refermable par ex.)
	 */
	toggleFolder(rowKey: any): boolean;
}

/** Etatde dossier d'un noeud. */
export enum EFolderState {
	/** none = Pas un dossier. */
		'none' = 'none',
	'closed' = 'closed',
	'opened' = 'opened',
	'opening' = 'opening'
}

export interface IGridDataTreeRow extends IGridDataRow {

	/** Retourne l'état de dossier de ce noeud. */
	getFolderState(): EFolderState

	isFolderStateSwitchable(): boolean

	/** Retourne la profondeur de ce noeud. 0 = noeud à la racine. */
	getDepth(): number

	/**
	 * Pour lui-même et chaque ancêtre, en remontant la hierarchie, retourne true si un frère suivant existe après ce noeud
	 * (ex: le boolean à l'offset 0 est à true si la row possède des frères suivants).
	 * Retourne null si l'implémentation ne peut retourner cette information.
	 */
	getNextSiblingAncestors(): boolean[] | null
}


/**
 * IGridDataHolder avec en source un tableau d'objets JSON.
 * Les fils des dossiers sont accessibles via la propriété childrenKey
 */
export class GridDataHolderJsonTree<D> extends GridDataHolderBase<D> implements IGridDataHolderTree {

	static defaultOpened(data: any) {return true}

	static defaultClosed(data: any) {return false}

	protected _fakeRow: GridDataRowJsonTree<D>;

	protected r1: GridDataRowJsonTree<D>;
	protected r2: GridDataRowJsonTree<D>;

	protected newDataRow(): GridDataRowJsonTree<D> {return new GridDataRowJsonTree(this, null)}

	/**
	 * @param childrenKey Nom de la propriété qui permet d'accéder aux noeuds fils dans les datas Json.
	 */
	constructor(public readonly childrenKey: string = '') {
		super();
	}

	defaultOpenState: (data: D) => boolean = GridDataHolderJsonTree.defaultClosed;

	setDefaultOpenState(fn: (data: D) => boolean): this {
		this.defaultOpenState = fn;
		return this;
	}

	/** Ouvre tout à l'initialisation des données. */
	setOpenAll(val: boolean): this {
		this.defaultOpenState = val ? GridDataHolderJsonTree.defaultOpened : GridDataHolderJsonTree.defaultClosed;
		return this;
	}

	openFolder(rowKey: any): boolean {
		if (!this.isRowKeyShown(rowKey)) {
			this.openFolder(this.parent(rowKey));
			if (!this.isRowKeyShown(rowKey)) return false;
		}
		const st = this.folderSt(rowKey);
		if (st === EFolderState.opening) return true;
		if (st !== EFolderState.closed) return false;
		const children: any[] = this.ch(rowKey);
		if (children === null) {
			this.openFolderAsync(rowKey);
			return true;
		}
		this.setFolderSt(rowKey, EFolderState.opened);
		let countSub = 0;
		for (let i = 0; i < children.length; i++) countSub += 1 + this.subRowsShown(children[i]);
		this.updateSubRowsShown(rowKey, countSub);
		const offset = this.getOffset(rowKey);
		if (this.grid) {
			this.grid.invalidateRows(offset, 1);
			this.grid.rowCountChanged(offset + 1, countSub);
		}
		return true;
	}

	/** A surcharger pour les ouvertures asynchrones (rowKey[this.childrenKey] === null). */
	async openFolderAsync(rowKey: any): Promise<boolean> {
		if (this.ch(rowKey) === null) {
			console.error("Method must be overriden!");
			return false;
		}
		return this.openFolder(rowKey);
	}

	/** Après un traitement asynchrone controle que ce rowKey soit toujours présent dans l'arbre. */
	protected isRowkeyAlive(rowKey: any) {
		return this.parent(rowKey) != null || this._datas.indexOf(rowKey) >= 0;
	}

	closeFolder(rowKey: any): boolean {
		const st = this.folderSt(rowKey);
		if (st !== EFolderState.opened && st !== EFolderState.opening) return false;
		this.setFolderSt(rowKey, EFolderState.closed);
		const delta = -this.subRowsShown(rowKey);
		this.updateSubRowsShown(rowKey, delta);
		const offset = this.getOffset(rowKey);
		this.grid.invalidateRows(offset, 1);
		if (delta !== 0) this.grid.rowCountChanged(offset + 1, delta);
		return true;
	}

	closeAllFolders(roots: D[] | null = this._datas, recursively?: boolean) {
		if (!roots) return;
		try {
			this.grid?.freezeRefresh();
			for (const d of roots) {
				if (this.folderSt(d) === EFolderState.opened) {
					if (recursively) this.closeAllFolders(this.ch(d), true);
					this.closeFolder(d);
				}
			}
		} finally {
			this.grid?.unfreezeRefresh();
		}
	}

	toggleFolder(rowKey: any): boolean {
		switch (this.folderSt(rowKey)) {
		case EFolderState.opened:
			return this.closeFolder(rowKey);
		case EFolderState.closed:
			return this.openFolder(rowKey);
		}
		return false;
	}


	setDatas(datas: D[], openIt?: (data: D) => boolean): this {
		const old = this._datas;
		if (old) for (const ch of old) this._killRowData(ch);
		for (const data of datas) this._initRowData(data, null, openIt || this.defaultOpenState);
		super.setDatas(datas);
		return this;
	}

	/**
	 * Renseigne ou remplace tous les fils d'un noeud.
	 * Les entrées sont triées en fonction de la clé de tri du grid courant.
	 */
	setChilrenTo(parentRowKey: any | null, children: D[], openIt?: (data: D) => boolean) {
		const sortFn = this._grid?.sortFn;
		if (sortFn) this._sortChildren(children, sortFn, true);
		let ch = parentRowKey ? this.ch(parentRowKey) : this._datas;
		this.updateDatasInTree(parentRowKey, 0, ch ? ch.length : 0, openIt, ...children);
	}

	/**
	 * Insère ou supprime des lignes dans une liste de fils.
	 * L'insertion doit prendre en compte l'ordre de tri courant du grid connecté ou l'ordre naturel de ce dataHolder.
	 *
	 * Attention, les entrées ne sont pas triées selon la clé de tri du grid courant.
	 *
	 * @param parentRowKey Noeud parent contenant la liste de fils ou null pour la liste racine du grid.
	 */
	updateDatasInTree(parentRowKey: any | null, start: number, deleteCount: number, openIt?: (data: D) => boolean, ...insertEntries: D[]) {
		let ch = parentRowKey ? this.ch(parentRowKey) : this._datas;
		if (ch == null) {
			if (ch === undefined) {
				//Transformation en dossier
				this.setFolderSt(parentRowKey, EFolderState.closed);
				this.setSubRowsShown(parentRowKey, 0);
			}
			ch = this.setCh(parentRowKey, []);
		}
		if (start < 0 || ch.length < start - 1) throw Error("Update insert " + start + " out of bounds 0-" + ch.length);
		if (start + deleteCount > ch.length) throw Error("Deletes " + deleteCount + " after " + start + " out of bounds 0-" + ch.length);

		//nb de rows affichées supprimées
		let deleteShown = 0;
		for (let i = start, m = start + deleteCount; i < m; i++) {
			const row = ch[i];
			deleteShown += 1 + this.subRowsShown(row);
			this._killRowData(row);
		}

		let insertRows = 0;
		if (insertEntries) for (const data of insertEntries) insertRows += this._initRowData(data, parentRowKey, openIt);

		ch.splice(start, deleteCount, ...insertEntries);

		if (parentRowKey && this.folderSt(parentRowKey) === EFolderState.opened) this.updateSubRowsShown(parentRowKey, insertRows - deleteShown);

		if (!this._grid) return;
		//On recherche l'offset graphique du début de la zone modifiée, -1 si zone masquée.
		const offset = this.getOffsetInChildren(parentRowKey, ch, start);
		if (offset >= 0) this._grid.spliceRows(offset, deleteShown, insertRows);
	}

	/** */
	deleteRowKey(rowKey: any): boolean {
		const p = this.parent(rowKey);
		const children = p ? this.ch(p) : this._datas;
		const offset = children.indexOf(rowKey);
		if (offset < 0) return false;
		this.updateDatasInTree(p, offset, 1);
		return true;
	}

	/**
	 * Remplace une ligne à la même position dans l'arbre.
	 * ATTENTION : Réinsère au même index, donc la position doit rester inchangée
	 * avec l'ordre de tri courant du grid. => Ne pas utiliser si l'algo de tri peut
	 * dépendre des changements de props entre l'oldData et newData.
	 */
	replaceRowKey(currentRowKey: any, newData: D, preserveChildren: boolean): boolean {
		const parent = this.parent(currentRowKey);
		const siblings = parent ? this.ch(parent) : this._datas;
		const index = siblings.indexOf(currentRowKey);
		if (index < 0) return false;
		if (preserveChildren) {
			const ch = this.ch(currentRowKey);
			siblings[index] = newData;
			this.setParent(newData, parent);
			if (ch !== undefined) {
				//C'est bien un dossier
				this.setCh(newData, ch);
				this.setSubRowsShown(newData, this.subRowsShown(currentRowKey));
				this.setFolderSt(newData, this.folderSt(currentRowKey));
				if (ch !== null) for (const c of ch) this.setParent(c, newData);
			}
			//this._killRowData(currentRowKey); NON casse les refs des descendants
			if (this._grid) {
				const offset = this.getOffset(newData);
				if (offset >= 0) this._grid.invalidateRows(offset, 1);
			}
		} else {
			this.updateDatasInTree(parent, index, 1, this.defaultOpenState, newData);
		}
		return true;
	}

	/**
	 * Insert un nouvel élément en trouvant l'offset en fonction de la clé de tri, insert à la fin sinon.
	 *
	 * @param parentRowKey Noeud parent contenant la liste de fils ou null pour insertion à la liste racine du grid.
	 */
	insertRowKey(parentRowKey: any | null, newData: D) {
		let children: D[];
		if (parentRowKey) {
			children = this.ch(parentRowKey);
			if (!children) children = this.setCh(parentRowKey, []);
		} else {
			children = this._datas;
		}
		this.updateDatasInTree(parentRowKey, this.findInsertPoint(children, newData), 0, this.defaultOpenState, newData);
	}

	/** Nombre de lignes actuellement **visibles** */
	countRows() {
		let count = 0;
		for (let i = 0; i < this._datas.length; i++) {
			count += 1 + this.subRowsShown(this._datas[i]);
		}
		return count;
	}

	findVisibleData(filter: (d: D) => boolean, inCh?: D[]): D | null {
		const ch = inCh || this._datas;
		for (const dCh of ch) {
			if (filter(dCh)) return dCh;
			if (this.folderSt(dCh) === EFolderState.opened) {
				const subCh = this.ch(dCh);
				if (subCh) {
					const r = this.findVisibleData(filter, subCh);
					if (r != null) return r;
				}
			}
		}
		return null;
	}

	/**
	 * Parcours toute la hiérarchie des rows affichées en indiquant l'offset courant dans le grid.
	 * Permet par exemple de réaffecter une ancienne sélection efficacement.
	 */
	visitRows(cb: (offset: number, data: D) => void) {
		const visit = (list: D[], offset: number): number => {
			for (let i = 0; i < list.length; i++) {
				const node = list[i];
				cb(offset++, node);
				if (this.folderSt(node) === EFolderState.opened) offset = visit(this.ch(node), offset);
			}
			return offset;
		}
		visit(this._datas, 0);
	}

	getDataByOffset(offset: number): D | null {
		if (offset >= 0) {
			let list = this._datas;
			let listIdx = 0;
			let data = list[listIdx] as any;
			let currOffset = 0;
			while (data && currOffset < offset) {
				currOffset++;
				const countCh = this.subRowsShown(data);
				if (countCh > 0) {
					if (countCh + currOffset > offset) {
						//on descend dans les fils
						list = this.ch(data);
						listIdx = 0;
						data = list[listIdx];
					} else {
						//on saute les fils jusqu'au frère suivant.
						currOffset += countCh;
						data = list[++listIdx];
					}
				} else {
					data = list[++listIdx];
				}
			}
			return data as D;
		}
		return null;
	}


	getRow(offset: number): GridDataRowJsonTree<D> | null {
		if (offset >= 0) {
			const data = this.getDataByOffset(offset);
			if (!data) return null;
			this._fakeRow.rowDatas = data;
			return this._fakeRow;
		}
		return null;
	}

	getRowKey(offset: number): D | null {
		return this.getDataByOffset(offset);
	}

	getOffset(rowKey: any): number {
		let offset = 0;
		let child = rowKey;
		let parent = this.parent(child);
		let list: any = parent ? this.ch(parent) : this._datas;
		do {
			if (parent && this.subRowsShown(parent) === 0) return -1; //dossier clos
			for (let i = 0; i < list.length; i++) {
				if (list[i] === child) break;
				offset += 1 + this.subRowsShown(list[i]);
			}
			if (parent) {
				offset++;
				child = parent;
				parent = this.parent(parent);
				list = parent ? this.ch(parent) : this._datas;
			} else {
				list = null;
			}
		} while (list);
		return offset;
	}

	/** Recherche l'offset graphique d'un point précis dans l'arbre, -1 si c'est dans une zone masquée (dossier ancêtre fermé).*/
	getOffsetInChildren(parentRowKey: any | null, children: D[], index: number): number {
		if (index === 0) {
			if (parentRowKey) {
				const offset = this.getOffset(parentRowKey);
				if (offset < 0) return offset;
				if (offset >= 0 && this.folderSt(parentRowKey) === EFolderState.opened) return offset + 1;
				return -1;
			}
			return 0;
		}
		const previous = children[index - 1] as any;
		const offset = this.getOffset(previous);
		if (offset < 0) return offset;
		return offset + this.subRowsShown(previous) + 1;
	}

	getRowKeyFolderState(rowKey: any): EFolderState {return this.folderSt(rowKey) || EFolderState.none}

	isRowKeyFolderStateSwitchable(rowKey: any): boolean {return true}

	getRowKeyParent(rowKey: any): D {return this.parent(rowKey)}

	getRowKeyChildren(rowKey: any): D[] {
		return rowKey ? this.ch(rowKey as D) : null;
	}

	/** Retourne l'index d'un rowKey dans son parent. -1 si pas trouvé. */
	getRowKeyIndex(rowKey: any): number {
		if (!rowKey) return -1;
		const p = this.parent(rowKey);
		const ch = p ? this.ch(p) : this._datas;
		if (!ch) return -1;
		return ch.indexOf(rowKey);
	}

	isRowKeyAncestor(ancRowKey: any, descRowKey: any): boolean {
		let p = this.parent(descRowKey);
		while (p) {
			if (p === ancRowKey) return true;
			p = this.parent(p);
		}
		return false;
	}

	/** Evalue si ce rowKey est actuellement affiché (ie tous ses ancêtres sont ouverts). */
	isRowKeyShown(rowKey: any): boolean {
		let p = this.parent(rowKey);
		while (p) {
			if (this.folderSt(p) !== EFolderState.opened) return false;
			p = this.parent(p);
		}
		return true;
	}

	/** Assure que la hiérarchie parent de rowKey soit open. */
	forceRowKeyShown(rowKey: any) {
		let p = this.parent(rowKey);
		while (p) {
			if (this.folderSt(p) !== EFolderState.opened) this.openFolder(p);
			p = this.parent(p);
		}
	}

	/**
	 * Pour les constructions de dossiers async, efface et referme le contenu d'un dossier qui n'est pas affiché.
	 * ATTENTION : à n'appeler que si isRowKeyShown() sur un fils === false.
	 */
	protected resetAsyncChildren(rowKey: any) {
		const chList = this.ch(rowKey);
		if (chList === undefined) return; //pas un dossier.
		this.setFolderSt(rowKey, EFolderState.closed);
		this.updateSubRowsShown(rowKey, -this.subRowsShown(rowKey));
		if (chList !== null) {
			for (const ch of chList) this._killRowData(ch);
			this.setCh(rowKey, null); //null == fils non chargés
		}
	}

	protected updateSubRowsShown(rowKey: any, delta: number) {
		if (delta === 0 || !rowKey) return;
		do {
			this.setSubRowsShown(rowKey, this.subRowsShown(rowKey) + delta);
			rowKey = this.parent(rowKey);
		} while (rowKey && this.folderSt(rowKey) === EFolderState.opened);
	}

	/** initialisation du grid qui DOIT être dans un état vierge.*/
	protected _tryBuildDatas() {
		if (!this._grid) return;
		if (this._datas.length > 0) {
			//tri initial avant la synchro avec le grid.
			const sortFn = this._grid.sortFn;
			if (sortFn) {
				//Tri selon la clé de tri du grid.
				this._sortChildren(this._datas, sortFn, true);
			}
			this._grid.rowCountChanged(0, this.countRows());
		}
	}


	protected _sortDatas(datas: any[], sortFn: IRowSortFn): void {
		this._sortChildren(datas, sortFn, true);
	}

	protected _sortChildren(array: any[], sortFn: IRowSortFn, recursive: boolean): void {
		this._initRowDatas();
		array.sort((d1: any, d2: any): number => {
			this.r1.rowDatas = d1;
			this.r2.rowDatas = d2;
			return sortFn(this.r1, this.r2);
		});
		if (recursive) for (let i = 0; i < array.length; i++) {
			const ch = this.ch(array[i]);
			if (ch && ch.length > 0) this._sortChildren(ch, sortFn, true);
		}
	}

	protected _initRowData(data: D, parent: D | null, openIt: (data: any) => boolean): number {
		if (parent) this.setParent(data, parent);
		const children = this.ch(data);
		if (children !== undefined) {
			if (openIt && openIt(data)) {
				let countDesc = 0;
				this.setFolderSt(data, EFolderState.opened);
				if (children !== null) for (const ch of children) countDesc += this._initRowData(ch, data, openIt);
				this.setSubRowsShown(data, countDesc);
				return countDesc + 1;
			}
			this.setFolderSt(data, EFolderState.closed);
			if (children !== null) for (const ch of children) this._initRowData(ch, data, openIt);
			this.setSubRowsShown(data, 0);
		}
		return 1;
	}

	/** Tue la ref au parent marquant le noeud déconnecté. */
	protected _killRowData(data: D) {
		//help GC, casse les cycles.
		this.setParent(data, undefined);
		const children = this.ch(data);
		if (children) for (const ch of children) this._killRowData(ch);
	}

	/**
	 * Retourne les noeuds fils d'un parent.
	 * Si undefined : parent n'en n'est pas un, c'est un noeud terminal, pas un dossier
	 * Si null : parent est bien un dossier mais fils pas encore chargés, cf openFolder() et openFolderAsync()
	 */
	ch(parent: D): D[] | null | undefined {return (parent as any)[this.childrenKey] as D[]}

	/** Remplace les noeuds fils d'un parent. */
	setCh(parent: D, ch: D[] | null | undefined): D[] | null | undefined {
		(parent as any)[this.childrenKey] = ch;
		return ch;
	}

	/**
	 * Retourne le parent d'un fils ou undefined si ligne à la racine de l'arbre.
	 */
	parent(data: D): D | undefined {return (data as any)[parentSym]}

	protected setParent(child: D, parent: D | undefined): D | undefined {
		(child as any)[parentSym] = parent;
		return parent;
	}

	/**
	 * Etat ouvert / clos d'un dossier.
	 * Si undefined : état iconnu.
	 */
	folderSt(data: D): EFolderState | undefined {return (data as any)[folderStateSym]}

	protected setFolderSt(data: D, s: EFolderState | undefined): EFolderState | undefined {
		(data as any)[folderStateSym] = s;
		return s;
	}

	subRowsShown(parent: D): number {return (parent as any)[subRowsShownSym] || 0}

	protected setSubRowsShown(parent: D, count: number): void {
		(parent as any)[subRowsShownSym] = count;
	}
}

/** Symbole pour accéder à l'état d'un dossier. */
const folderStateSym = Symbol('folderStateSym');

/** Symbole pour accéder au folder parent. */
const parentSym = Symbol('parentSym');

/** Symbole pour accéder au nombre de descendants ouverts d'un folder. */
const subRowsShownSym = Symbol('subRowsShownSym');


/**
 * IGridDataTreeRow trivial fondé sur un objet Json, utilisé pendant le tri
 * et sous forme d'instance singleton pour les cellBuilders.
 */
export class GridDataRowJsonTree<D> extends GridDataRowJson<D> implements IGridDataTreeRow {

	dataHolder: GridDataHolderJsonTree<D>

	constructor(dataHolder: GridDataHolderJsonTree<D>, rowDatas: D) {super(dataHolder, rowDatas);}

	getFolderState(): EFolderState {return this.dataHolder.getRowKeyFolderState(this.rowDatas)}

	isFolderStateSwitchable(): boolean {return this.dataHolder.isRowKeyFolderStateSwitchable(this.rowDatas)}

	/** Retourne la profondeur de ce noeud. 0 = noeud à la racine. */
	getDepth(): number {
		let depth = 0;
		for (let parent = this.dataHolder.parent(this.rowDatas); parent; parent = this.dataHolder.parent(parent)) depth++;
		return depth;
	}

	/**
	 * Retourne, pour chaque ancêtre, si un frère suivant existe après ce noeud.
	 * Retourne null si l'implémentation ne peut retourner cette information.
	 */
	getNextSiblingAncestors(): boolean[] | null {
		const result: boolean[] = [];
		let child = this.rowDatas;
		for (let parent = this.dataHolder.parent(child); parent; parent = this.dataHolder.parent(parent)) {
			const children = this.dataHolder.ch(parent);
			result.push(children[children.length - 1] !== child);
			child = parent;
		}
		return result
	}

}

/**
 * Mixin pour un GridDataHolderJsonTree qui permet de ne pas modifier
 * les datas D qui lui sont fournies (affectations d'états internes gérés par GridDataHolderJsonTree).
 * Seuls les changements structurels dans les datas (liste des fils d'un parent) entraine des modifications dans les datas D.
 *
 * @see MxGridUntouchedDatas
 */
export function MxGridTreeUntouchedDatas<T extends Constructor<GridDataHolderJsonTree<D>>, D extends Object>(Base: T): T {
	return class MxGridTreeUntouchedDatas extends MxGridUntouchedDatas(Base) {

		protected intProps = new WeakMap<D, IGridTreeInternalProps<D>>();

		parent(data: D): D | undefined {return this.intProps.get(data)?.parent}

		protected setParent(child: D, parent: D | undefined): D | undefined {
			let p = this.intProps.get(child);
			if (!p) this.intProps.set(child, (p = Object.create(null)));
			p.parent = parent;
			return parent;
		}

		folderSt(data: D): EFolderState | undefined {return this.intProps.get(data)?.folderSt}

		protected setFolderSt(data: D, s: EFolderState | undefined): EFolderState | undefined {
			let p = this.intProps.get(data);
			if (!p) this.intProps.set(data, (p = Object.create(null)));
			p.folderSt = s;
			return s;
		}

		subRowsShown(parent: D): number {return this.intProps.get(parent)?.subRowsShown || 0}

		protected setSubRowsShown(parent: D, count: number) {
			let p = this.intProps.get(parent);
			if (!p) this.intProps.set(parent, (p = Object.create(null)));
			p.subRowsShown = count;
		}

		protected _killRowData(data: D) {
			//cas de recyclages de data dans la hierarchie qui peuvent poser pb.
			//on laisse le GC faire le travail.
			// const children = this.ch(data);
			// this.intProps.delete(data);
			// if (children) for (const ch of children) this._killRowData(ch);
		}
	}
}

//export class GridDataHolderJsonTreeSafe<D extends Object> extends MxGridTreeUntouchedDatas(GridDataHolderJsonTree)<D>{}

/** Propriétés internes d'une row dans un tree. */
export interface IGridTreeInternalProps<D> extends IGridRowInternalProps<D> {
	parent?: D | undefined
	folderSt?: EFolderState | undefined
	subRowsShown?: number
}

/** CellBuilder pour la colonne de type "tree". */
export class CellBuilderTreeRow implements ICellBuilder {

	drawTreeLines: boolean = false;
	childrenIndent: number;
	onDblclick: 'toggle' /*default*/ | 'open' | 'none'

	constructor(public col: GridCol, public labelBuilder: ICellBuilder, childrenIndent?: number, onDblclick?: 'toggle' /*default*/ | 'open' | 'none') {
		this.childrenIndent = childrenIndent || .8;
		if (onDblclick) this.onDblclick = onDblclick;
	}

	/** Active le dessinement des lignes de l'arbre. */
	setDrawTreeLines(draw: boolean): this {
		this.drawTreeLines = draw;
		return this;
	}

	/** Spécifie l'indentation en 'em' de l'arbre lorsque les lignes ne sont pas dessinées. */
	setChildrenIndent(width: number): this {
		this.childrenIndent = width;
		return this;
	}

	redrawCell(row: IGridDataTreeRow, root: HTMLElement): void {
		const struct = root as ICellTree;
		if (!struct.twisty) {
			//construction de la structure
			struct.twisty = root.appendChild(document.createElement('x-twisty'));
			struct.box = root.appendChild(document.createElement('span'));
			struct.twisty.onpointerdown = toggleFolderTwisty;
			struct.twisty.onclick = struct.twisty.ondblclick = function (this: HTMLElement, ev) {if (!this.classList.contains("none")) ev.stopPropagation()};
			switch (this.onDblclick) {
			case "none":
				break;
			case "open":
				struct.ondblclick = openFolderCell;
				break;
			default:
				struct.ondblclick = toggleFolderCell;
			}
		}
		struct.rowKey = row.rowKey;
		DOM.setAttr(struct.twisty, 'class', row.isFolderStateSwitchable() ? row.getFolderState() : 'none');
		const siblings: boolean[] = this.drawTreeLines ? row.getNextSiblingAncestors() : null;
		if (siblings == null) {
			//pas de dessin des lignes
			const width = (row.getDepth() * this.childrenIndent) + 'em';
			DOM.setStyle(struct.twisty, 'margin-inline-start', width);
		} else if (siblings.length > 0) {
			//dessin des lignes de l'arbre.
			let space = struct.twisty.previousElementSibling;
			if (!space) space = root.insertBefore(document.createElement('x-space'), root.firstChild);
			DOM.setAttr(space, 'class', siblings[0] ? 'T' : 'L');
			space = space.previousElementSibling;
			for (let i = 1; i < siblings.length; i++) {
				if (!space) space = root.insertBefore(document.createElement('x-space'), root.firstChild);
				DOM.setAttr(space, 'class', siblings[i] ? 'I' : null);
				space = space.previousElementSibling;
			}
			//on suppr les spaces en trop
			while (space) {
				const prev = space.previousElementSibling;
				space.remove();
				space = prev;
			}
		}
		this.labelBuilder.redrawCell(row, struct.box);
	}

	getColSortFn(): IRowSortFn | null {
		return this.labelBuilder.getColSortFn();
	}
}

export interface IGridColTree extends GridCol {

	grid: IGridTree;

	/** mémoire du skin installé pour le tree. */
	treeSkin: HTMLStyleElement;
}

/** Propriétés sur le root de la cell. */
export interface ICellTree extends HTMLElement {
	rowKey: any;
	twisty: HTMLElement;
	box: HTMLElement;
}

function toggleFolderCell(this: ICellTree, ev: MouseEvent) {
	if (this.classList.contains("none")) return;
	(DOMSH.findHost<IGrid>(this).dataHolder as IGridDataHolderTree).toggleFolder(this.rowKey);
}

function openFolderCell(this: ICellTree, ev: MouseEvent) {
	if (this.classList.contains("none")) return;
	(DOMSH.findHost<IGrid>(this).dataHolder as IGridDataHolderTree).openFolderAsync(this.rowKey);
}

function toggleFolderTwisty(this: HTMLElement, ev: MouseEvent) {
	if (this.classList.contains("none")) return;
	ev.stopPropagation();
	(DOMSH.findHost<IGrid>(this).dataHolder as IGridDataHolderTree).toggleFolder((this.parentNode as ICellTree).rowKey);
}

function onKeydown(this: IGridTree, ev: KeyboardEvent) {
	//console.log("onKeypress::::", ev);
	const activeRow = this.getActiveRow();
	if (activeRow < 0) return;
	const row = this.dataHolder.getRow(activeRow);
	if (!row) return;
	switch (ev.key) {
	case 'ArrowLeft' :
		if (window.getComputedStyle(this).direction === 'rtl') {
			if (!this.dataHolder.openFolder(row.rowKey)) {
				this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowDown'}));
				return;
			}
		} else {
			if (!this.dataHolder.closeFolder(row.rowKey)) {
				this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowUp'}));
				return;
			}
		}
		this.ensureRowVisible(activeRow);
		break;
	case 'ArrowRight' :
		if (window.getComputedStyle(this).direction === 'rtl') {
			if (!this.dataHolder.closeFolder(row.rowKey)) {
				this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowUp'}));
				return;
			}
		} else {
			if (!this.dataHolder.openFolder(row.rowKey)) {
				this.dispatchEvent(new KeyboardEvent('keydown', {key: 'ArrowDown'}));
				return;
			}
		}
		this.ensureRowVisible(activeRow);
		break;
	case 'Enter' :
		if (!this.dataHolder.toggleFolder(row.rowKey)) return;
		this.ensureRowVisible(activeRow);
		break;
	default :
		return;
	}
	if (this.shadowRoot.activeElement !== null) this.focus(); //on remet le focus sur la grid si il était sur un btn du header par ex.
}

/** Surcharge de la gestion du drag pour gérer le drop-marker*/
function onDragOverInScrollArea(this: HTMLElement, ev: DragEvent) {
	GridSmall.onDragOverInScrollArea.call(this, ev);
	const grid = DOMSH.findHost(this) as GridSmall & IGridTree;
	const line = grid._dropMark.parentElement;
	if (line && line.classList.contains('line')) {
		//décalage du drop-mark
		const treeCell = grid._getCellInLine(line, grid.treeCol);
		const twisty = treeCell.firstElementChild as HTMLElement;
		let left = twisty.offsetLeft + twisty.offsetWidth - line.offsetLeft;
		if (grid._dropMark.getAttribute('pos') === 'after' && twisty.className !== 'none') {
			//après un dossier = en fils (qu'il soit ouvert ou fermé).
			left += twisty.offsetWidth;
		}
		DOM.setStyleStart(grid._dropMark, left + "px");
		//ouverture du dossier avec un timer
		if (!grid.dragOverTimer && twisty.classList.contains('closed')) {
			//Dossier qu'on devrait ouvrir au bout d'un timeout.
			const rowKey = grid.dataHolder.getRow(grid._getOffset(line)).rowKey;
			grid.dragOverTimer = setTimeout(() => {
				grid.dataHolder.openFolder(rowKey);
			}, 800);
		}
	} else {
		DOM.setStyleStart(grid._dropMark, "0px");
	}
}

function onDropMarkChange(this: IGridTree) {
	if (this.dragOverTimer) {
		clearTimeout(this.dragOverTimer);
		this.dragOverTimer = 0;
	}
}

/* TODO dessin des lignes (balises x-space)*/
// x-space {
// 	padding-inline-end: .8em;
// 	color: #DAE8E8;
// }
//
// x-space.T:before {
// 	content: '\u251C';
// 	position: absolute;
// }
//
// x-space.I:before {
// 	content: '\u2502';
// 	position: absolute;
// }
//
// x-space.L:before {
// 	content: '\u2514';
// 	position: absolute;
// }
REG.reg.registerSkin('c-grid/tree', 1, /* language=CSS */ `
	x-twisty {
		padding-inline-start: 1em;
		unicode-bidi: isolate;
		cursor: pointer;
		filter: var(--filter);
	}

	x-twisty.closed {
		background: url(/@skin@/commons/widgets/grid/closed.svg) no-repeat center / 1em;
	}

	x-twisty.opened {
		background: url(/@skin@/commons/widgets/grid/opened.svg) no-repeat center / 1em;
	}

	x-twisty.closed:hover,
	x-twisty.opened:hover {
		filter: var(--hover-filter);
		background-color: var(--row-inSel-unfocus-bgcolor);
	}

	x-twisty.none {
		background: none;
	}
`);

/*x-twisty.opening {
	todo background: url(/@skin@/commons/widgets/grid/opening.svg) no-repeat center / 1em;
}*/


/**
 * Organise une liste à plat de données d'un grid (provider:GridDataHolderJsonArray<D>)
 * en un arbre catégorisé selon un IGridCategorizer<D, E>, cette vue catégorisée étant
 * exploitable dans target:GridDataHolderJsonTree<E>.
 *
 * Les modifications des données doivent être réalisées exclusivement dans le provider,
 * jamais dans le GridDataHolderJsonTree cible.
 */
export class GridArrayToTree<D, E> {

	protected _categorizer: IGridCategorizer<D, E>;

	protected _provider: GridDataHolderJsonArray<D>;
	protected _target: GridDataHolderJsonTree<E>;
	protected _idMap: Map<any, E> = new Map();
	protected _lstn: (start: number, deleteCount: number, ...insertEntries: D[]) => void;

	get categorizer(): IGridCategorizer<D, E> {return this._categorizer}

	get target(): GridDataHolderJsonTree<E> {return this._target}

	get provider(): GridDataHolderJsonArray<D> {return this._provider}

	constructor(target?: GridDataHolderJsonTree<E>) {
		this._target = target || new GridDataHolderJsonTree();
		this._lstn = this.onWillUpdate.bind(this);
	}

	setCategorizer(categorizer: IGridCategorizer<D, E>): this {
		this._categorizer = categorizer;
		this.tryBind();
		return this;
	}

	setProvider(provider: GridDataHolderJsonArray<D>): this {
		if (this._provider) this._provider.onWillUpdate.delete(this._lstn);
		this._provider = provider;
		this.tryBind();
		return this;
	}

	getTargetEntryById(id: any): E {
		return this._idMap.get(id);
	}

	protected tryBind() {
		if (this._categorizer && this._provider) {
			//Init
			this.rebuildAll(this._provider.getDatas());
			//Listen
			(this._provider.onWillUpdate || (this._provider.onWillUpdate = new EventMgr())).add(this._lstn);
		}
	}

	protected onWillUpdate(start: number, deleteCount: number, ...insertEntries: D[]) {
		if (start === 0 && deleteCount === this._provider.countRows()) {
			//cas particulier du reset All
			this.rebuildAll(insertEntries);
			return;
		}
		const categ = this._categorizer;
		//Suppression des entrées
		for (let i = start, end = start + deleteCount; i < end; i++) {
			const d = this._provider.getDataByOffset(i);
			const id = categ.getId(d);
			const e = this._idMap.get(id);
			if (e) {
				this._idMap.delete(id);
				let p = this._target.getRowKeyParent(e);
				this._target.deleteRowKey(e);
				while (p) {
					const ch = this._target.getRowKeyChildren(p);
					if ((!ch || ch.length === 0) && categ.shouldRemoveEmptyFolder(p)) {
						this._idMap.delete(categ.getId(p));
						const newP = this._target.getRowKeyParent(p);
						this._target.deleteRowKey(p);
						p = newP;
					} else break;
				}
			}
		}
		if (!insertEntries) return;
		//Insertion des entrées
		const eList: E[] = [];
		for (const d of insertEntries) {
			const e = categ.wrapForTree(d);
			if (e) {
				eList.push(e);
				this._idMap.set(categ.getId(d), e);
			} else {
				//Suppression de cette entrée
				this._idMap.delete(categ.getId(d));
			}
		}
		for (const e of eList) {
			const stack = categ.getAncestorIds(e);
			let parent: E = null;
			if (stack) for (let i = stack.length - 1; i >= 0; i--) {
				parent = this._idMap.get(stack[i]);
				if (parent) {
					//1er parent trouvé
					for (i++; i < stack.length; i++) {
						//Création des dossiers intermédiaires
						const subP = categ.getFolder(stack[i]);
						this._idMap.set(stack[i], subP);
						this._target.insertRowKey(parent, subP);
						parent = subP;
					}
					break;
				}
			}
			this._target.insertRowKey(parent, e);
		}
	}

	protected rebuildAll(srcData: D[]) {
		const categ = this._categorizer;

		//Reset
		this._idMap.clear();
		if (!srcData || srcData.length === 0) {
			this._target.setDatas([]);
			return;
		}

		//Construction de la map
		for (const d of srcData) {
			const e = categ.wrapForTree(d);
			if (e) this._idMap.set(categ.getId(d), e);
		}
		//Construction de l'arbre
		const dst: E[] = [];
		for (const e of this._idMap.values()) {
			let list: E[];
			const stack = categ.getAncestorIds(e);
			if (stack) {
				let i = stack.length - 1;
				for (; i >= 0; i--) {
					const parent = this._idMap.get(stack[i]);
					if (parent) {
						// @ts-ignore
						list = this._target.ch(parent);
						// @ts-ignore
						if (!list) list = this._target.setCh(parent, []);
						break;
					}
				}
				if (!list) {
					//aucun dossier ancêtre  trouvé, on va les créer à la racine.
					list = dst;
				}
				for (i++; i < stack.length; i++) {
					//Création des dossiers intermédiaires
					const subP = categ.getFolder(stack[i]);
					this._idMap.set(stack[i], subP);
					//list.push(subP); NON sera ajouté via le parcours de _idMap
					list = this._target.ch(subP);
					if (!list) list = this._target.setCh(subP, []);
				}
			}
			(list || dst).push(e);
		}
		this._target.setDatas(dst);
	}
}

export interface IGridCategorizer<D, E> {

	getId(data: D | E): any

	/** A minima : return Object.create(data) ou null pour forcer l'élimination de cette entrée. */
	wrapForTree(data: D): E | null

	getAncestorIds(data: E): null | any[]

	/** Si la catégorisation consiste à créer des dossiers qui ne sont pas issus de la source de données. */
	getFolder(id: any): E

	/** Devrait retourner true si c'est un dossier virtuel de catégorsation. */
	shouldRemoveEmptyFolder(folder: E): boolean
}
